import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Headline, TextLink, Text, Icon, Divider } from '@oneaudi/unified-web-components';
import { useSelector } from 'react-redux';
import {
  AprOrPayment,
  BonusOffer,
  DownPayment,
  MainOffer,
  OffersWrapper,
  PaymentLabel,
  Term,
  PaymentNumber,
} from './VehicleInfo.styles';
import { OfferDetails } from '../../types';
import OffersTag from '../../assets/icons/OfferTagIcon';
import { numberFormatter, rateTransform } from '../../functions/priceFormat';
import {
  PercentData,
  Apr,
  Percent,
  PaymentData,
  TermCa,
  MainOfferData,
  OfferWrapper,
  DividerWrapper,
} from './Offers.styles';
import { selectAppConfig } from '../../redux/appConfigReducer';

interface OffersProps {
  offers: OfferDetails;
  offerType: string;
}

const Offers: React.FC<OffersProps> = ({ offers, offerType }) => {
  const { t, i18n } = useTranslation();

  const { market } = useSelector(selectAppConfig);
  const isUSMarket = market?.toLowerCase().includes('us');

  const isFinanceOffer = offerType === 'finance';

  const monthlyPayment = Number(offers.monthlyPayment?.replace(/[$,]/g, ''));

  const lang = i18n.language;

  const { aprRate, term, customerDownPayment, paymentFrequency, creditBonus, type, cpoTerm } =
    offers;

  const paymentFreq = useMemo(() => {
    if (paymentFrequency === 'WEEKLY') return t('digitalLeads.vehicle.offers.weeklyPayment');
    if (paymentFrequency === 'BI-WEEKLY') return t('digitalLeads.vehicle.offers.biWeeklyPayment');
    return t('digitalLeads.vehicle.offers.monthlyPayment');
  }, [paymentFrequency, t]);

  const termForCPO = cpoTerm && cpoTerm.replace('apr', '').replace('_', ' - ');

  const offersNewCA = useMemo(() => {
    return (
      <OfferWrapper>
        <MainOfferData>
          <PercentData>
            <Text
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t('digitalLeads.vehicle.offers.apr')}
            </Text>
            <Percent data-testid="offer-new-apr">
              {aprRate && (
                <Headline
                  tag="h4"
                  variant={{
                    order: '4',
                    type: 'headline',
                    weight: 'bold',
                  }}
                >
                  {`${(aprRate * 100).toFixed(2)}%`}
                </Headline>
              )}
            </Percent>
            <Text
              data-testid="offer-new-term"
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t('digitalLeads.vehicle.offers.upTo')} {term}{' '}
              {t('digitalLeads.vehicle.offers.months')}
            </Text>
          </PercentData>
          <DividerWrapper>
            <Divider orientation="vertical" />
          </DividerWrapper>
          <PaymentData>
            <Text
              data-testid="offer-new-freq"
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {paymentFreq}
            </Text>
            <Percent>
              <Headline
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'bold',
                }}
              >
                {numberFormatter(Number((monthlyPayment ?? 0).toFixed()), lang)}
              </Headline>
            </Percent>
            <TermCa>
              <Text
                data-testid="offer-new-downpayment"
                variant={{
                  order: '2',
                  style: 'normal',
                }}
              >
                {t('digitalLeads.vehicle.offers.with')} {numberFormatter(customerDownPayment, lang)}{' '}
                {t('digitalLeads.vehicle.offers.downPayment')}
              </Text>
            </TermCa>
          </PaymentData>
        </MainOfferData>
        {!!creditBonus && (
          <BonusOffer>
            <Icon size="s" name="special-offer" />
            <Headline
              tag="h4"
              variant={{
                order: '4',
                type: 'headline',
                weight: 'bold',
              }}
            >
              {numberFormatter(creditBonus, lang)} {t('digitalLeads.vehicle.offers.bonusOffer')}
            </Headline>
          </BonusOffer>
        )}
      </OfferWrapper>
    );
  }, [aprRate, creditBonus, customerDownPayment, lang, monthlyPayment, paymentFreq, t, term]);

  const offersCpoCA = useMemo(() => {
    return (
      <OfferWrapper>
        <PercentData>
          <Text
            variant={{
              order: '2',
              style: 'normal',
            }}
          >
            {t('digitalLeads.vehicle.offers.apr')}
          </Text>
          <Percent data-testid="offer-new-apr">
            {aprRate && (
              <Headline
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'bold',
                }}
              >
                {`${(aprRate * 100).toFixed(2)}%`}
              </Headline>
            )}
          </Percent>
          <Text
            data-testid="offer-new-term"
            variant={{
              order: '2',
              style: 'normal',
            }}
          >
            {termForCPO} {t('digitalLeads.vehicle.offers.months')}
          </Text>
        </PercentData>
      </OfferWrapper>
    );
  }, [aprRate, t, termForCPO]);

  const offersUS = useMemo(() => {
    return (
      <>
        <MainOffer>
          <AprOrPayment isFinanceOffer={isFinanceOffer}>
            <PaymentNumber>
              {isFinanceOffer && offers.aprRate
                ? `${(offers.aprRate * 100).toFixed(2)}%`
                : numberFormatter(monthlyPayment, lang)}
            </PaymentNumber>
            <PaymentLabel>
              {isFinanceOffer
                ? t('digitalLeads.vehicle.offers.apr')
                : t('digitalLeads.vehicle.offers.monthlyPayment')}
            </PaymentLabel>
          </AprOrPayment>
          <Term isFinanceOffer={isFinanceOffer}>
            <PaymentNumber>{offers.term}</PaymentNumber>
            <PaymentLabel>{t('digitalLeads.vehicle.offers.months')}</PaymentLabel>
          </Term>
          {!isFinanceOffer && (
            <DownPayment>
              <PaymentNumber>{numberFormatter(offers.customerDownPayment, lang)}</PaymentNumber>
              <PaymentLabel>{t('digitalLeads.vehicle.offers.downPayment')}</PaymentLabel>
            </DownPayment>
          )}
        </MainOffer>
        {!!offers.creditBonus && (
          <BonusOffer>
            <OffersTag /> {numberFormatter(offers.creditBonus, lang)}{' '}
            {t('digitalLeads.vehicle.offers.bonusOffer')}
          </BonusOffer>
        )}
      </>
    );
  }, [
    isFinanceOffer,
    lang,
    monthlyPayment,
    offers.aprRate,
    offers.creditBonus,
    offers.customerDownPayment,
    offers.term,
    t,
  ]);

  const offersCA = type === 'NEW-CAR' ? offersNewCA : offersCpoCA;

  const RenderOffers = isUSMarket ? offersUS : offersCA;

  return <OffersWrapper>{RenderOffers}</OffersWrapper>;
};

export default Offers;
